import { createContext, useContext, useEffect, useState } from "react";
import type { ShoppingCartProviderProps, ShoppingCartContext, ShippingItem, CardItem, Product, PaymentFeesItem, ActivationCostsItem } from "../types/shoppingCart";

import { OrderSchema } from "../zodSchemas/Order";
import { ZodIssue } from "zod";
const PRODUCTS_URL = "https://xrpl-labs.com/tangem-v2/cards:data.json"
const ShoppingCartContext = createContext({} as ShoppingCartContext);

export const tiers = {
    0: 0,
    5: 1,
    10: 2,
    15: 3
}

export function useShoppingCart() {
    return useContext(ShoppingCartContext);
};



export function ShoppingCartProvider({ children }: ShoppingCartProviderProps) {
    const [cartItems, setCartItems] = useState<(CardItem | ShippingItem | PaymentFeesItem | ActivationCostsItem)[]>([])
    const [tier, setTier] = useState<number>(0);
    const [orderErrors, setOrderErrors] = useState<ZodIssue[] | null | undefined>(null)
    const [shippingHasError, setShippingHasError] = useState<boolean>(false)
    const [oldShippingPrice, setOldShippingPrice] = useState<number>(0);
    const [products, setProducts] = useState<{ products: Product[] }[]>([]);

    const orderTotal = cartItems.reduce((total, item) => total + (item.type === 'card' ? getItemPrice(item.id, tier) * item.quantity : item.price), 0)
    const shippingCosts = cartItems.reduce((total, item) => total + (item.type === "shipping" ? item.price : 0), 0)
    const subTotal = cartItems.reduce((total, item) => total + (item.type === 'card' ? getItemPrice(item.id, tier) * item.quantity : 0), 0)
    let cardTotal = cartItems.reduce((total, item) => total + (item.type === 'card' ? item.quantity : 0), 0)

    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetch(PRODUCTS_URL)
            .then((res) => res.json())
            .then((data) => {
                setProducts([data]);
                setLoading(false);
            })
            .catch(() => { setLoading(false) });
    }, []);

    /**
     * Update tier based on amount of cards in cart
     */
    useEffect(() => {
        let totalQuantity = cartItems.reduce((total, item) => total + (item.type === 'card' ? item.quantity : 0), 0)
        let newTier = 0
        Object.keys(tiers).forEach((key) => {
            if (totalQuantity >= parseInt(key)) {
                // @ts-ignore
                newTier = tiers[key];
            }
        })
        if (tier !== newTier) {
            updateCardPrices(newTier)
        }
        setTier(newTier);

        //
        if (checkIfCartHasSupporterPack()) {
            cardTotal = cardTotal + 4
        }
    }, [cartItems])

    function getItemPrice(id: number | string, tier: number): number {
        let price: number = 0;

        products.forEach(productGroup => {
            const product = productGroup.products.find(item => item.id === id);
            if (product) {
                price = product.prices[tier];
            }
        });
        return price;
    }

    function increaseCartQuantity(id: number | string, title: string, specialPrice?: number) {
        setCartItems(currItems => {
            if (currItems.find(item => item.id === id) == null) {
                return [...currItems, { id, quantity: 1, type: 'card', price: specialPrice ? specialPrice : getItemPrice(id, tier), title }]
            } else {
                return currItems.map(item => {
                    if (item.id === id && item.type === 'card') {
                        return { ...item, quantity: item.quantity + 1 }
                    } else {
                        return item;
                    }
                })
            }
        })
        // Supporter pack added, so update shipping costs with 0
        if (id === 2) {
            if (cartHasShippingItems()) {
                const shippingItem: ShippingItem | undefined = getShippingItem();
                if (!shippingItem) return;
                setOldShippingPrice(shippingItem.price)
                shippingItem.price = 0;
                updateShippingCosts(shippingItem, 'Supporter Pack Free Delivery');
            }
        }
    }
    function decreaseCartQuantity(id: number | string) {
        setCartItems(currItems => {
            if (currItems.find(item => item.id === id && item.type === 'card')?.quantity === 1) {
                return currItems.filter(item => item.id !== id)
            } else {
                return currItems.map(item => {
                    if (item.id === id && item.type === 'card') {
                        return { ...item, quantity: item.quantity - 1 }
                    } else {
                        return item
                    }
                })
            }
        })
        if (id === 2 && cartHasShippingItems()) {
            const shippingItem: ShippingItem | undefined = getShippingItem();
            if (!shippingItem) return;
            shippingItem.price = oldShippingPrice;

            updateShippingCosts(shippingItem, 'Shipping');
        }
    }
    function removeFromCart(id: number | string) {
        setCartItems(currItems => {
            return currItems.filter(item => item.id !== id);
        });
    }
    function checkIfCartHasSupporterPack() {
        return cartItems.some(item => item.type === 'card' && item.id === 2)
    }
    function memoizeShippingCosts(price: number) {
        setOldShippingPrice(price);
    }
    function cartHasCardItems() {
        return cartItems.some(item => item.type === 'card') && getTotalCardQuantity() >= 2
    }
    function getTotalCardQuantity() {
        return cartItems.reduce((total, item) => total + (item.type === 'card' ? item.quantity : 0), 0)
    }

    /**
     * Card functions
     */
    function updateCardPrices(tier: number) {
        setCartItems(currItems => {
            return currItems.map(item => {
                if (item.type === 'card') {
                    return { ...item, price: getItemPrice(item.id, tier) }
                } else {
                    return item
                }
            })
        })
    }
    function getItemQuantity(id: number | string) {
        return cartItems.find(item => item.type === 'card' && item.id === id)?.quantity || 0
    }
    function getOrderLineTotal(id: number | string) {
        return getItemPrice(id, tier) * getItemQuantity(id)
    }
    function getCardTotalPerProductGroup(index: number) {
        let itemsInProduct = products[index].products;
        let total = 0;
        itemsInProduct.forEach(item => {
            total += cartItems.find(cartItem => cartItem.id === item.id)?.quantity || 0
        })
        return total;
    }

    /**
     * Shipping functions
     */
    function updateShippingCosts(shippingCosts: ShippingItem, title: string) {
        if (!shippingCosts) return;
        //@ts-ignore
        setCartItems(currItems => {
            setShippingHasError(false);
            if (currItems.find(item => item.type === 'shipping') == null) {
                return [...currItems, { ...shippingCosts, code: shippingCosts.code, quantity: 1, title }]
            } else {
                return currItems.map(item => {
                    if (item.type === 'shipping') {
                        return { ...shippingCosts, code: shippingCosts.code, quantity: 1, title }
                    } else {
                        return item
                    }
                })

            }
        })
    }

    function getShippingCosts() {
        return cartItems.reduce((total, item) => total + (item.type === "shipping" ? item.price : 0), 0)
    }
    function getShippingItem() {
        return cartItems.find(item => item.type === "shipping")
    }
    function cartHasShippingItems() {
        return cartItems.reduce((total, item) => total + (item.type === "shipping" ? item.price : 0), 0) > 0 || checkIfCartHasSupporterPack() === true
    }

    /**
     * Activation costs functions
     */

    function addActivationCosts(activationCosts: ActivationCostsItem) {
        setCartItems(currItems => {
            if (currItems.find(item => item.type === 'activationCosts') == null) {
                return [...currItems, { ...activationCosts, quantity: 1, title: activationCosts.title }]
            } else {
                return currItems.map(item => {
                    if (item.type === 'activationCosts') {
                        return { ...activationCosts, quantity: 1, title: activationCosts.title }
                    } else {
                        return item
                    }
                })
            }
        })
    }

    function checkIfOrderHasActivationCosts() {
        return cartItems.some(item => item.type === 'activationCosts')
    }

    function getActivationCosts() {
        return cartItems.reduce((total, item) => total + (item.type === "activationCosts" ? item.price : 0), 0)
    }

    /**
     * Order validation
     * Order can be type any, because we want to validate the inputs
     */
    function validateOrderObject(order: any) {
        setShippingHasError(false);
        setOrderErrors(null);

        const hasError = OrderSchema.safeParse(order);
        if (hasError.error) {
            setOrderErrors(hasError.error.issues)
            return false;
        }

        let orderHasShippingItems = cartHasShippingItems();
        if (!orderHasShippingItems) {
            setShippingHasError(true);
            return false;
        }

        return true;
    }
    function fieldHasError(field: string) {
        if (!orderErrors) return false;
        return orderErrors.filter(item => item.path.includes(field))[0] ? true : false;
    }
    function getFieldError(field: string) {
        return orderErrors?.filter(item => item.path.includes(field))[0].message || ''
    }

    if (loading) {
        return <></>;
    }
    if (!products || products.length === 0) {
        return <div>Failed to load products.</div>;
    }
    return (
        <ShoppingCartContext.Provider value={{
            getItemQuantity,
            increaseCartQuantity,
            decreaseCartQuantity,
            removeFromCart,
            getOrderLineTotal,
            cartItems,
            cardTotal,
            shippingCosts,
            subTotal,
            orderTotal,
            tier,
            categories: products,
            updateShippingCosts,
            validateOrderObject,
            fieldHasError,
            getFieldError,
            getCardTotalPerProductGroup,
            checkIfCartHasSupporterPack,
            getShippingCosts,
            memoizeShippingCosts,
            shippingHasError,
            cartHasShippingItems,
            cartHasCardItems,
            setCartItems,
            addActivationCosts,
            checkIfOrderHasActivationCosts,
            getActivationCosts
        }}>
            {children}
        </ShoppingCartContext.Provider>
    );
}
